"use client";

import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { OrderItemRow } from "./OrderItemRow";
import { OrderTypeSelector } from "./OrderTypeSelector";

interface OrderItemModifier {
  name: string;
  price: number;
}

interface OrderLineItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  modifiers: OrderItemModifier[];
  notes?: string;
}

interface OrderPanelProps {
  items: OrderLineItem[];
  orderType: string;
  tableNumber?: string;
  onUpdateQuantity: (index: number, qty: number) => void;
  onRemoveItem: (index: number) => void;
  onClearOrder: () => void;
  onSendToKitchen: () => void;
  onPay?: () => void;
  onChangeOrderType: (type: string) => void;
  subtotal: number;
  taxAmount: number;
  total: number;
  loading?: boolean;
  orderSent?: boolean;
}

export function OrderPanel({
  items,
  orderType,
  tableNumber,
  onUpdateQuantity,
  onRemoveItem,
  onClearOrder,
  onSendToKitchen,
  onPay,
  onChangeOrderType,
  subtotal,
  taxAmount,
  total,
  loading,
  orderSent,
}: OrderPanelProps) {
  const isEmpty = items.length === 0;

  return (
    <div className="flex h-full flex-col bg-surface/50">
      {/* Header */}
      <div className="border-b border-white/8 px-4 py-3 space-y-3 shrink-0">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Current Order</h2>
          {tableNumber && (
            <span className="rounded-lg bg-white/8 px-2.5 py-1 text-xs font-medium text-white">
              Table {tableNumber}
            </span>
          )}
        </div>
        <OrderTypeSelector value={orderType} onChange={onChangeOrderType} />
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto px-4">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            {orderSent ? (
              <>
                <svg className="h-12 w-12 text-success mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-base text-white font-medium">Order sent to workshop</p>
                <p className="text-sm text-text-muted mt-1">Tap Pay to process payment</p>
              </>
            ) : (
              <>
                <svg className="h-12 w-12 text-surface-border-2 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                </svg>
                <p className="text-base text-text-muted">No items yet</p>
                <p className="text-sm text-text-dim mt-1">Tap menu items or use voice to add</p>
              </>
            )}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {items.map((item, index) => (
              <OrderItemRow
                key={index}
                item={item}
                index={index}
                onUpdateQuantity={onUpdateQuantity}
                onRemoveItem={onRemoveItem}
              />
            ))}
          </div>
        )}
      </div>

      {/* Totals and Actions */}
      <div className="border-t border-white/8 px-4 py-4 space-y-4 shrink-0">
        {(!isEmpty || orderSent) && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-text-muted">Subtotal</span>
              <span className="text-white/80">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-text-muted">Tax</span>
              <span className="text-white/80">{formatCurrency(taxAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/8">
              <span className="text-white">Total</span>
              <span className="text-white">{formatCurrency(total)}</span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="md"
            onClick={onClearOrder}
            disabled={isEmpty && !orderSent}
            className={cn(
              "flex-1 text-danger hover:text-red-300 hover:bg-danger/10",
              "disabled:text-text-dim disabled:hover:bg-transparent",
            )}
          >
            {orderSent ? "New Order" : "Clear"}
          </Button>
          {orderSent ? (
            <Button
              variant="primary"
              size="xl"
              onClick={onPay}
              className="flex-[2] bg-success hover:bg-emerald-600"
            >
              Pay {formatCurrency(total)}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="xl"
              onClick={onSendToKitchen}
              disabled={isEmpty}
              loading={loading}
              className="flex-[2]"
            >
              Send to Workshop
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
